import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Plus } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { uploadToFtp } from "../../../lib/uploadToFtp";
import UploadProgress from "../../../components/UploadProgress";

interface VideoItem {
  id: string;
  title: string;
  src: string;
}

interface GalleryItem {
  id: string;
  url: string;
  file?: File;
  isNew: boolean;
}

const EditUsedBoat: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [availableVideos, setAvailableVideos] = useState<VideoItem[]>([]);

  const [formData, setFormData] = useState({
    Title: "",
    Description: "",
    FullInfo: "",
    Price: "",
    BoatLength: "",
    BoatWidth: "",
    BoatCapacity: "",
    BoatWeight: "",
    MotorPower: "",
    DefaultEquipments: "",
    ExtraEquipments: "",
    CatalogImage: "",
    Videos: [] as string[],
  });

  const [catalogFile, setCatalogFile] = useState<File | null>(null);
  const [catalogPreview, setCatalogPreview] = useState<string | null>(null);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, error } = await supabase.from('used_boats').select('*').eq('id', id).single();
        if (error) throw error;

        const { data: videosData } = await supabase.from('videos').select('*');
        if (videosData) {
          setAvailableVideos(videosData.map(v => ({ id: v.id, title: v.title, src: v.url })));
        }

        // Legacy: derive MotorPower from MinMotorPower/MaxMotorPower if MotorPower missing
        const legacyMotor = data.MotorPower
          || (data.MinMotorPower && data.MaxMotorPower ? `${data.MinMotorPower} - ${data.MaxMotorPower} HP` : (data.MaxMotorPower || data.MinMotorPower || ""));

        setFormData({
          Title: data.Title || "",
          Description: data.Description || "",
          FullInfo: data.FullInfo || "",
          Price: data.Price || "",
          BoatLength: data.BoatLength || "",
          BoatWidth: data.BoatWidth || "",
          BoatCapacity: data.BoatCapacity || "",
          BoatWeight: data.BoatWeight || "",
          MotorPower: legacyMotor,
          DefaultEquipments: data.DefaultEquipments ? data.DefaultEquipments.join(", ") : "",
          ExtraEquipments: data.ExtraEquipments ? data.ExtraEquipments.join(", ") : "",
          CatalogImage: data.CatalogImage || "",
          Videos: data.Videos || [],
        });
        setCatalogPreview(data.CatalogImage);

        if (data.Images && Array.isArray(data.Images)) {
          setGalleryItems(data.Images.map((url: string) => ({
            id: url,
            url: url,
            isNew: false,
          })));
        }
      } catch (err) {
        console.error(err);
        toast.error("Tekne bilgileri alınamadı");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleCatalogChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setCatalogFile(file);
      setCatalogPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      const newItems: GalleryItem[] = files.map(file => ({
        id: URL.createObjectURL(file),
        url: URL.createObjectURL(file),
        file: file,
        isNew: true,
      }));
      setGalleryItems(prev => [...prev, ...newItems]);
    }
  };

  const removeGalleryItem = (idToRemove: string) => {
    setGalleryItems(prev => prev.filter(item => item.id !== idToRemove));
  };

  const toggleVideo = (videoSrc: string) => {
    setFormData(prev => {
      const exists = prev.Videos.includes(videoSrc);
      if (exists) {
        return { ...prev, Videos: prev.Videos.filter(v => v !== videoSrc) };
      }
      return { ...prev, Videos: [...prev.Videos, videoSrc] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let catalogPath = formData.CatalogImage;

      if (catalogFile) {
        setUploading(true);
        setUploadProgress(0);
        setUploadFileName(catalogFile.name);
        catalogPath = await uploadToFtp(catalogFile, "used-boats", undefined, (p) => setUploadProgress(p));
      }

      const finalGalleryUrls: string[] = [];
      const filesToUpload = galleryItems.filter(i => i.isNew && i.file);
      let currentIndex = 0;

      for (const item of galleryItems) {
        if (item.isNew && item.file) {
          setUploadFileName(item.file.name);
          setUploadProgress(0);
          setUploadCurrent(++currentIndex);
          setUploadTotal(filesToUpload.length);
          const url = await uploadToFtp(item.file, "used-boats", undefined, (p) => setUploadProgress(p));
          finalGalleryUrls.push(url);
        } else {
          finalGalleryUrls.push(item.url);
        }
      }
      setUploading(false);

      const defaultEqArray = formData.DefaultEquipments.split(",").map(s => s.trim()).filter(s => s);
      const extraEqArray = formData.ExtraEquipments.split(",").map(s => s.trim()).filter(s => s);

      const { error } = await supabase.from('used_boats').update({
        Title: formData.Title,
        Description: formData.Description,
        FullInfo: formData.FullInfo,
        Price: formData.Price,
        BoatLength: formData.BoatLength,
        BoatWidth: formData.BoatWidth,
        BoatCapacity: formData.BoatCapacity,
        BoatWeight: formData.BoatWeight,
        MotorPower: formData.MotorPower,
        CatalogImage: catalogPath,
        DefaultEquipments: defaultEqArray,
        ExtraEquipments: extraEqArray,
        Images: finalGalleryUrls,
        Videos: formData.Videos,
      }).eq('id', id);

      if (error) throw new Error("İlan güncellenemedi: " + error.message);

      toast.success("İlan başarıyla güncellendi");
      navigate("/panel/used-boats");

    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.Title) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button onClick={() => navigate("/panel/used-boats")} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">İlan Düzenle</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {uploading && <UploadProgress progress={uploadProgress} fileName={uploadFileName} currentFile={uploadCurrent} totalFiles={uploadTotal} />}

          {/* Catalog Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Katalog Görseli</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors cursor-pointer relative"
              onClick={() => document.getElementById('catalog-upload')?.click()}>
              {catalogPreview ? (
                <img src={catalogPreview} className="h-64 object-contain" alt="Preview" />
              ) : (
                <div className="text-center"><Upload className="mx-auto h-12 w-12 text-gray-400" /><span>Resim Seç</span></div>
              )}
              <input id="catalog-upload" type="file" className="sr-only" accept="image/*" onChange={handleCatalogChange} />
            </div>
          </div>

          {/* Gallery Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Galeri Görselleri</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {galleryItems.map((item, index) => (
                <div key={item.id} className="relative aspect-square rounded-lg overflow-hidden border bg-white shadow-sm">
                  {item.isNew && <div className="absolute top-0 left-0 bg-blue-500 text-white text-[10px] px-1 rounded-br z-10">YENİ</div>}
                  <img src={item.url} alt={`Galeri ${index}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeGalleryItem(item.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors z-10">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div
                className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors"
                onClick={() => document.getElementById('gallery-upload')?.click()}
              >
                <Plus className="text-gray-400 mb-2" size={24} />
                <span className="text-sm text-gray-500">Ekle</span>
              </div>
            </div>
            <input id="gallery-upload" type="file" multiple className="sr-only" accept="image/*" onChange={handleGalleryChange} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Başlık / Model</label><input required className="w-full border rounded p-2" value={formData.Title} onChange={e => setFormData({ ...formData, Title: e.target.value })} /></div>
            <div><label className="block text-sm font-medium mb-1">Fiyat</label><input required className="w-full border rounded p-2" value={formData.Price} onChange={e => setFormData({ ...formData, Price: e.target.value })} /></div>
          </div>

          <div><label className="block text-sm font-medium mb-1">Kısa Açıklama</label><input required className="w-full border rounded p-2" value={formData.Description} onChange={e => setFormData({ ...formData, Description: e.target.value })} /></div>
          <div><label className="block text-sm font-medium mb-1">Detaylı Bilgi</label><textarea required rows={4} className="w-full border rounded p-2" value={formData.FullInfo} onChange={e => setFormData({ ...formData, FullInfo: e.target.value })} /></div>

          <h3 className="font-bold text-lg border-b pb-2 mt-6">Teknik Özellikler</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Uzunluk</label><input className="w-full border rounded p-2" value={formData.BoatLength} onChange={e => setFormData({ ...formData, BoatLength: e.target.value })} /></div>
            <div><label className="block text-sm font-medium mb-1">Genişlik</label><input className="w-full border rounded p-2" value={formData.BoatWidth} onChange={e => setFormData({ ...formData, BoatWidth: e.target.value })} /></div>
            <div><label className="block text-sm font-medium mb-1">Kapasite</label><input className="w-full border rounded p-2" value={formData.BoatCapacity} onChange={e => setFormData({ ...formData, BoatCapacity: e.target.value })} /></div>
            <div><label className="block text-sm font-medium mb-1">Ağırlık</label><input className="w-full border rounded p-2" value={formData.BoatWeight} onChange={e => setFormData({ ...formData, BoatWeight: e.target.value })} /></div>
            <div className="col-span-2"><label className="block text-sm font-medium mb-1">Motor Gücü</label><input className="w-full border rounded p-2" value={formData.MotorPower} onChange={e => setFormData({ ...formData, MotorPower: e.target.value })} placeholder="Örn: 150 HP" /></div>
          </div>

          <div><label className="block text-sm font-medium mb-1">Donanım (Virgülle ayırın)</label><textarea rows={3} className="w-full border rounded p-2" value={formData.DefaultEquipments} onChange={e => setFormData({ ...formData, DefaultEquipments: e.target.value })} /></div>
          <div><label className="block text-sm font-medium mb-1">Ekstra Donanım (Virgülle ayırın)</label><textarea rows={3} className="w-full border rounded p-2" value={formData.ExtraEquipments} onChange={e => setFormData({ ...formData, ExtraEquipments: e.target.value })} /></div>

          {/* Video Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Videolar</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-60 overflow-y-auto p-2 border rounded">
              {availableVideos.map(video => (
                <div
                  key={video.id}
                  className={`cursor-pointer border-2 rounded-lg overflow-hidden relative ${formData.Videos.includes(video.src) ? 'border-blue-500' : 'border-transparent'}`}
                  onClick={() => toggleVideo(video.src)}
                >
                  <video src={video.src} className="w-full h-24 object-cover pointer-events-none" />
                  <div className="p-1 text-xs truncate bg-white">{video.title}</div>
                  {formData.Videos.includes(video.src) && (
                    <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center text-xs">✓</div>
                  )}
                </div>
              ))}
              {availableVideos.length === 0 && <div className="col-span-4 text-center text-gray-500 text-sm">Video bulunamadı. Önce video yükleyiniz.</div>}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button type="submit" disabled={loading} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg">
              {loading ? "Kaydediliyor..." : "Güncelle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUsedBoat;
